import { useTheme } from '../context/ThemeContext';
import { Background3D } from '../components/Background3D';
import { FileText, Scale, Users, Shield, AlertTriangle, Mail } from 'lucide-react';

export function Terms() {
  const { theme } = useTheme();

  const termsHighlights = [
    {
      icon: FileText,
      title: "Terms of Use",
      description: "By using our services, you agree to comply with these terms and conditions."
    },
    {
      icon: Scale,
      title: "Fair Usage",
      description: "Our services are provided for legitimate research and educational purposes."
    },
    {
      icon: Users,
      title: "User Conduct",
      description: "Users must maintain respectful and ethical behavior when using our platform."
    },
    {
      icon: Shield,
      title: "Intellectual Property",
      description: "All content and materials are protected by copyright and other intellectual property laws."
    }
  ];

  return (
    <div className="relative">
      <Background3D />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-semibold mb-8 leading-tight">
              Terms of <span className="text-gradient">Service</span>
            </h1>
            <p className={`text-xl leading-relaxed max-w-3xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Please read these Terms of Service carefully before using our website 
              and services. These terms govern your use of Huxley Research platform and services.
            </p>
            <div className="mt-8">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Last updated: September 7, 2025
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Terms Highlights */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-12 text-center">
            Key Terms Overview
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {termsHighlights.map((highlight, index) => (
              <div
                key={index}
                className={`p-6 rounded-2xl border transition-all duration-300 hover:scale-105 ${
                  theme === 'dark'
                    ? 'bg-dark-200/50 border-dark-300 hover:border-primary-500'
                    : 'bg-white/50 border-gray-200 hover:border-primary-300'
                } backdrop-blur-sm`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                  theme === 'dark' ? 'bg-primary-500/20' : 'bg-primary-100'
                }`}>
                  <highlight.icon className="w-6 h-6 text-primary-500" />
                </div>
                <h3 className="text-lg font-semibold mb-3">{highlight.title}</h3>
                <p className={`text-sm leading-relaxed ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {highlight.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Terms */}
      <section className={`py-20 ${
        theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'
      }`}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="space-y-12">
            
            {/* Acceptance of Terms */}
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/70'
            } backdrop-blur-sm`}>
              <h3 className="text-2xl font-semibold mb-6">1. Acceptance of Terms</h3>
              <p className={`leading-relaxed mb-4 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                By accessing and using the Huxley Research website and services, you accept and agree to be bound by the terms and provision of this agreement. These Terms of Service apply to all visitors, users, and others who access or use our services.
              </p>
              <p className={`leading-relaxed ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                If you do not agree to abide by the above, please do not use this service. We reserve the right to modify these terms at any time, and such modifications shall be effective immediately upon posting.
              </p>
            </div>

            {/* Use of Services */}
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/70'
            } backdrop-blur-sm`}>
              <h3 className="text-2xl font-semibold mb-6">2. Use of Services</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-medium mb-2">Permitted Use</h4>
                  <p className={`leading-relaxed ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    Our services are intended for research, educational, and legitimate business purposes. You may use our platform to access information about our AI models, research, and educational programs.
                  </p>
                </div>
                <div>
                  <h4 className="text-lg font-medium mb-2">Prohibited Activities</h4>
                  <p className={`leading-relaxed mb-3 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    You agree not to:
                  </p>
                  <ul className="space-y-2">
                    {[
                      "Use our services for any unlawful purpose or in violation of any applicable laws",
                      "Attempt to gain unauthorized access to our systems or data",
                      "Interfere with or disrupt the integrity or performance of our services",
                      "Upload or transmit viruses, malware, or other harmful code",
                      "Violate the intellectual property rights of Huxley Research or third parties",
                      "Harass, abuse, or harm other users or our staff"
                    ].map((item, index) => (
                      <li key={index} className={`flex items-start ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        <div className="w-2 h-2 rounded-full bg-red-500 mt-2 mr-3 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Intellectual Property */}
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/70'
            } backdrop-blur-sm`}>
              <h3 className="text-2xl font-semibold mb-6">3. Intellectual Property</h3>
              <p className={`leading-relaxed mb-4 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                The service and its original content, features, and functionality are and will remain the exclusive property of Huxley Research and its licensors. The service is protected by copyright, trademark, and other laws.
              </p>
              <p className={`leading-relaxed ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent. All other trademarks are the property of their respective owners.
              </p>
            </div>

            {/* User Accounts */}
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/70'
            } backdrop-blur-sm`}>
              <h3 className="text-2xl font-semibold mb-6">4. User Accounts</h3>
              <p className={`leading-relaxed mb-4 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                When you create an account with us, you must provide information that is accurate, complete, and current at all times. You are responsible for safeguarding the password and for maintaining the security of your account.
              </p>
              <ul className="space-y-3">
                {[
                  "You are responsible for all activities that occur under your account",
                  "You must notify us immediately of any unauthorized use of your account",
                  "We reserve the right to suspend or terminate accounts that violate these terms",
                  "Account information must be kept current and accurate"
                ].map((item, index) => (
                  <li key={index} className={`flex items-start ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    <div className="w-2 h-2 rounded-full bg-primary-500 mt-2 mr-3 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Privacy and Data */}
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/70'
            } backdrop-blur-sm`}>
              <h3 className="text-2xl font-semibold mb-6">5. Privacy and Data</h3>
              <p className={`leading-relaxed mb-4 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Your privacy is important to us. Please review our Privacy Policy, which also governs your use of our services, to understand our practices regarding the collection and use of your information.
              </p>
              <p className={`leading-relaxed ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                By using our services, you consent to the collection and use of information in accordance with our Privacy Policy.
              </p>
            </div>

            {/* Disclaimers */}
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/70'
            } backdrop-blur-sm`}>
              <h3 className="text-2xl font-semibold mb-6">6. Disclaimers and Limitations</h3>
              <div className={`p-4 rounded-lg mb-4 ${
                theme === 'dark' ? 'bg-yellow-500/10 border border-yellow-500/20' : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-yellow-600 mb-2">Important Notice</h4>
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-yellow-200' : 'text-yellow-700'
                    }`}>
                      Our services are provided "as is" without warranties of any kind, either express or implied.
                    </p>
                  </div>
                </div>
              </div>
              <ul className="space-y-3">
                {[
                  "We do not warrant that our services will be uninterrupted or error-free",
                  "We are not liable for any indirect, incidental, or consequential damages",
                  "Our liability is limited to the maximum extent permitted by law",
                  "You use our services at your own risk and discretion"
                ].map((item, index) => (
                  <li key={index} className={`flex items-start ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    <div className="w-2 h-2 rounded-full bg-primary-500 mt-2 mr-3 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Termination */}
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/70'
            } backdrop-blur-sm`}>
              <h3 className="text-2xl font-semibold mb-6">7. Termination</h3>
              <p className={`leading-relaxed mb-4 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                We may terminate or suspend your account and bar access to our services immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever, including but not limited to a breach of these Terms.
              </p>
              <p className={`leading-relaxed ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Upon termination, your right to use our services will cease immediately. All provisions of these Terms which by their nature should survive termination shall survive.
              </p>
            </div>

            {/* Contact Information */}
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/70'
            } backdrop-blur-sm`}>
              <h3 className="text-2xl font-semibold mb-6">8. Contact Information</h3>
              <p className={`leading-relaxed mb-4 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className={`p-4 rounded-lg ${
                theme === 'dark' ? 'bg-dark-300/50' : 'bg-gray-100'
              }`}>
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-primary-500 mr-3" />
                  <span className="font-medium">legal@noemaresearch.com</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Agreement Section */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <div className={`rounded-2xl p-12 ${
            theme === 'dark'
              ? 'bg-gradient-to-br from-dark-200 to-dark-300/50'
              : 'bg-gradient-to-br from-gray-50 to-white'
          }`}>
            <h2 className="text-3xl font-semibold mb-4">
              Agreement to Terms
            </h2>
            <p className={`text-lg mb-8 max-w-2xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              By continuing to use our services, you acknowledge that you have read, 
              understood, and agree to be bound by these Terms of Service.
            </p>
            <p className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              These terms are effective as of September 7, 2025
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
